/**
 * S3アップロード/削除用ユーティリティ
 */
import { api } from "~/api";
import { getCurrentCustomerIdSync } from "~/AtomWithDynamoDB";

/**
 * S3の署名付きURLを取得する
 * @param key S3キー
 * @returns 署名付きURL or null
 */
export async function getSignedUrlFromS3(key: string): Promise<string | null> {
  try {
    console.info("[S3Utils] 署名付きURL取得開始:", key);
    const result = await api.s3.getSignedUrl.query({ key });
    if (result.success && result.url) {
      return result.url;
    }
    return null;
  } catch (error) {
    console.error("[S3Utils] 署名付きURL取得エラー:", key, error);
    return null;
  }
}

/**
 * 複数画像の署名付きURLをchunkサイズごとに並列処理で取得する
 * @param images keyとsrcプロパティを持つオブジェクトの配列
 * @param chunkSize 1回の並列処理で扱う画像数
 * @returns 署名付きURLに変換されたオブジェクト配列
 */
export async function getSignedUrlsInChunks<T extends { key: string; src: string }>(
  images: T[],
  chunkSize: number = 10
): Promise<T[]> {
  const results: T[] = [];

  // chunkサイズごとに分割して処理
  for (let i = 0; i < images.length; i += chunkSize) {
    const chunk = images.slice(i, i + chunkSize);

    // 各chunkを並列処理
    const chunkResults = await Promise.all(
      chunk.map(async (img) => {
        console.info("[S3Utils] 処理中:", img.key, img.src);
        const signedUrl = await getSignedUrlFromS3(img.src);
        return {
          ...img,
          src: signedUrl || img.src, // 失敗時は元のsrcを使用
        };
      })
    );

    results.push(...chunkResults);
  }

  return results;
}

/**
 * ファイルから拡張子を抽出する
 */
const extractExtension = (file: File | string): string => {
  if (typeof file === "string") {
    const mimeMatch = file.match(/^data:(image\/[a-zA-Z0-9.+-]+);base64,/);
    if (mimeMatch) {
      const mimeType = mimeMatch[1];
      return mimeType.split("/")[1];
    }
    return "png";
  } else {
    const ext = file.type.split("/").pop();
    return ext || "png";
  }
};

/**
 * ファイルをbase64に変換する
 */
const convertToBase64 = async (file: File | string): Promise<string> => {
  if (typeof file === "string") {
    if (!/^data:image\/[a-zA-Z0-9.+-]+;base64,.+/.test(file)) {
      throw new Error("base64形式が不正です");
    }
    return file;
  } else {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }
};

/**
 * S3へ画像をアップロード(ロゴ用)
 * @param file File型 or base64文字列
 * @returns S3保存パス
 */
export async function uploadImageToS3(
  file: File | string,
  dir: string,
  customerId?: string // オプション引数に変更
): Promise<string> {
  // customerIdが指定されていない場合は、atomから取得
  const finalCustomerId = customerId || getCurrentCustomerIdSync();
  if (!finalCustomerId) {
    throw new Error("Customer ID is not available. Please ensure the user is authenticated.");
  }

  const uuid = crypto.randomUUID();
  const ext = extractExtension(file);
  const base64 = await convertToBase64(file);

  // 例: a3badb9d-f2f5-6ee2-5e70-71928c3ad0da/logos/f93edae6-9df1-471a-8589-4090e883620e.png
  const key = `${finalCustomerId}/${dir}/${uuid}.${ext}`;

  try {
    // tRPC経由でS3アップロード
    const result = await api.s3.uploadImage.mutate({ key, base64 });
    // 型ガードでAPIレスポンスを厳密にチェック
    function isS3UploadResponse(obj: unknown): obj is { success: boolean } {
      return (
        typeof obj === "object" &&
        obj !== null &&
        "success" in obj &&
        typeof (obj as { success?: unknown }).success === "boolean"
      );
    }
    if (!isS3UploadResponse(result)) {
      throw new Error("APIレスポンスが不正です");
    }
    if (!result.success) throw new Error("S3アップロード失敗");
    return key;
  } catch (error) {
    console.error("[S3Utils] アップロードエラー:", error);
    throw error;
  }
}

/**
 * 画像ファイルの複数一括アップロード(フロアマップ用)
 * @param files File型またはbase64文字列の配列
 * @param dir S3ディレクトリ名（例: floorImages）
 * @returns S3保存パス配列
 */
export async function uploadImagesToS3(
  files: (File | string)[],
  dir: string,
  customerId?: string // オプション引数に変更
): Promise<string[]> {
  // customerIdが指定されていない場合は、atomから取得
  const finalCustomerId = customerId || getCurrentCustomerIdSync();
  if (!finalCustomerId) {
    throw new Error("Customer ID is not available. Please ensure the user is authenticated.");
  }

  // 複数ファイルを並列アップロード
  return Promise.all(files.map((file) => uploadImageToS3(file, dir, finalCustomerId)));
}

/**
 * S3のファイルを削除する
 * @param key バケット以降のキー
 * @returns 削除成功: true, 失敗: false
 */
export async function deleteFileFromS3(key: string): Promise<boolean> {
  try {
    // tRPC経由でS3削除
    const result = await api.s3.delete.mutate({ key });
    return result.success;
  } catch (error) {
    console.error("[S3Utils] 削除エラー:", error);
    return false;
  }
}
